import { trackEvent } from "./amplitude";
import {
  consumeArticleSignupAttribution,
  ARTICLE_SIGNUP_POPUP_SOURCE,
} from "./articleSignupAttribution";
import { capturePostHogEvent } from "./posthog";

/**
 * Web-app decision events. PostHog receives the snake_case name.
 * Amplitude receives the Title Case name with the same properties.
 */
const AMPLITUDE_EVENT_NAMES = {
  symbol_opened: "Symbol Opened",
  paywall_viewed: "Paywall Viewed",
  paywall_upgrade_clicked: "Paywall Upgrade Clicked",
  signup_started: "Signup Started",
  signup_completed: "User Signed Up",
  signup_failed: "Signup Failed",
  sign_in_failed: "Sign In Failed",
  subscription_cancelled: "Subscription Cancelled",
  email_verification_failed: "Email Verification Failed",
  password_reset_requested: "Password Reset Requested",
  password_reset_completed: "Password Reset Completed",
  password_reset_failed: "Password Reset Failed",
  profile_completed: "Profile Completed",
  report_opened: "Report Opened",
  portfolio_link_copied: "Portfolio Link Copied",
  lesson_started: "Lesson Started",
  lesson_completed: "Lesson Completed",
  notification_clicked: "Notification Clicked",
  promo_code_failed: "Promo Code Failed",
  article_signup_popup_viewed: "Article Signup Popup Viewed",
  article_signup_popup_signup_clicked: "Article Signup Popup Signup Clicked",
  article_signup_popup_dismissed: "Article Signup Popup Dismissed",
  article_signup_popup_signed_up: "Article Signup Popup Signed Up",
} as const;

type ProductEventName = keyof typeof AMPLITUDE_EVENT_NAMES;

export type AuthMethod = "email" | "google" | "apple";
export type PasswordResetStep = "request" | "otp" | "set";

let previousPathname = "";
let currentPathname = "";

/** Remember the in-app path the user just left. `document.referrer` stays external in this SPA. */
export function recordPathname(pathname: string): void {
  if (pathname === currentPathname) return;
  previousPathname = currentPathname;
  currentPathname = pathname;
}

export function getPreviousPathname(): string {
  return previousPathname;
}

export function symbolOpenSource(previousPath: string): string {
  if (!previousPath) return "direct";
  if (previousPath.startsWith("/stock-screener")) return "screener";
  if (previousPath.startsWith("/top-halal")) return "top_halal";
  if (previousPath.startsWith("/dividend")) return "dividend";
  if (previousPath.startsWith("/market-news")) return "news";
  if (previousPath.startsWith("/article")) return "article";
  if (previousPath.startsWith("/insider")) return "insider";
  if (previousPath.startsWith("/screening")) return "screening";
  if (previousPath === "/") return "home";
  return "other";
}

export function promoFailureReason(errorCode?: string): string {
  if (errorCode === "referred_only" || errorCode === "first_paid_plan_only") {
    return errorCode;
  }
  return "invalid";
}

function cleanProperties(
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  const entries = Object.entries(properties).filter(
    ([, value]) => value !== undefined && value !== "",
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function capture(
  name: ProductEventName,
  properties?: Record<string, unknown>,
): void {
  const payload = cleanProperties(properties);
  capturePostHogEvent(name, payload);
  trackEvent(AMPLITUDE_EVENT_NAMES[name], payload);
}

export function trackSymbolOpened(properties: {
  symbol: string;
  halal_status: string;
  source: string;
  row_position?: number;
}): void {
  capture("symbol_opened", properties);
}

export function trackPaywallViewed(properties: {
  feature_key?: string;
  symbol?: string;
  category?: string;
  surface: string;
  /** The paywall offered the free trial instead of an upgrade. */
  trial_offer?: boolean;
}): void {
  capture("paywall_viewed", properties);
}

export function trackPaywallUpgradeClicked(properties: {
  feature_key?: string;
  symbol?: string;
  category?: string;
  destination: string;
  trial_offer?: boolean;
}): void {
  capture("paywall_upgrade_clicked", properties);
}

export function trackSignupStarted(method: AuthMethod): void {
  capture("signup_started", { method });
}

/** Fired once when an account is actually created. Dual-sends to PostHog and Amplitude. */
export function trackSignupCompleted(method: AuthMethod): void {
  const attribution = consumeArticleSignupAttribution();
  const articleSource = attribution
    ? {
        source: ARTICLE_SIGNUP_POPUP_SOURCE,
        article_slug: attribution.article_slug,
        article_id: attribution.article_id,
      }
    : undefined;

  capture("signup_completed", { method, ...articleSource });

  if (attribution) {
    capture("article_signup_popup_signed_up", {
      method,
      article_slug: attribution.article_slug,
      article_id: attribution.article_id,
    });
  }
}

export function trackArticleSignupPopupViewed(properties: {
  article_slug: string;
  article_id: string;
}): void {
  capture("article_signup_popup_viewed", properties);
}

export function trackArticleSignupPopupSignupClicked(properties: {
  article_slug: string;
  article_id: string;
}): void {
  capture("article_signup_popup_signup_clicked", properties);
}

export function trackArticleSignupPopupDismissed(properties: {
  article_slug: string;
  article_id: string;
}): void {
  capture("article_signup_popup_dismissed", properties);
}

export function trackSignupFailed(method: AuthMethod, errorCode: string): void {
  capture("signup_failed", { method, error_code: errorCode });
}

export function trackSignInFailed(method: AuthMethod, errorCode: string): void {
  capture("sign_in_failed", { method, error_code: errorCode });
}

export function trackSubscriptionCancelled(properties: {
  plan_id?: number;
  plan_name?: string;
  reason: string;
}): void {
  capture("subscription_cancelled", properties);
}

export function trackEmailVerificationFailed(reason: string): void {
  capture("email_verification_failed", { reason });
}

export function trackPasswordResetRequested(): void {
  capture("password_reset_requested");
}

export function trackPasswordResetCompleted(): void {
  capture("password_reset_completed");
}

export function trackPasswordResetFailed(step: PasswordResetStep): void {
  capture("password_reset_failed", { step });
}

export function trackProfileCompleted(hasCountry: boolean): void {
  capture("profile_completed", { has_country: hasCountry });
}

export function trackReportOpened(properties: {
  report_id: string;
  report_type?: string;
  symbol?: string;
}): void {
  capture("report_opened", properties);
}

export function trackPortfolioLinkCopied(hasHoldings: boolean): void {
  capture("portfolio_link_copied", { has_holdings: hasHoldings });
}

export function trackLessonStarted(properties: {
  course_id: string;
  lesson_id: string;
}): void {
  capture("lesson_started", properties);
}

export function trackLessonCompleted(properties: {
  course_id: string;
  lesson_id: string;
}): void {
  capture("lesson_completed", properties);
}

export function trackNotificationClicked(properties: {
  notification_type: string;
  destination?: string;
  resolved: boolean;
}): void {
  capture("notification_clicked", properties);
}

export function trackPromoCodeFailed(reason: string): void {
  capture("promo_code_failed", { reason });
}
